// The ecosystem policy's closed vocabularies and its row as Effect Schema. The policy record and
// its lookups live in `policy.ts` without importing Effect, so browser bundles that only read the
// record stay free of Schema; they take this file's types only.
import { Schema } from 'effect';
import { Ecosystem } from './ecosystem.ts';
import {
  AUTHORISATION_SERVER_REALMS,
  CATALOGUE_FAMILY_GROUPINGS,
  CATALOGUE_PHASE_LABEL_SOURCES,
  CATALOGUE_SCOPE_AUTHORITIES,
  DIRECTORY_BOUNDED_CONTEXTS,
  ECOSYSTEM_REGULATORS,
  SOFTWARE_STATEMENT_EXPIRIES,
  SOFTWARE_STATEMENT_ORG_IDENTIFIERS,
} from './members.ts';

export const EcosystemRegulator = Schema.Literals(ECOSYSTEM_REGULATORS);
export type EcosystemRegulator = typeof EcosystemRegulator.Type;

export const AuthorisationServerRealm = Schema.Literals(AUTHORISATION_SERVER_REALMS);
export type AuthorisationServerRealm = typeof AuthorisationServerRealm.Type;

export const DirectoryBoundedContext = Schema.Literals(DIRECTORY_BOUNDED_CONTEXTS);
export type DirectoryBoundedContext = typeof DirectoryBoundedContext.Type;

export const SoftwareStatementOrgIdentifier = Schema.Literals(SOFTWARE_STATEMENT_ORG_IDENTIFIERS);
export type SoftwareStatementOrgIdentifier = typeof SoftwareStatementOrgIdentifier.Type;

export const SoftwareStatementExpiry = Schema.Literals(SOFTWARE_STATEMENT_EXPIRIES);
export type SoftwareStatementExpiry = typeof SoftwareStatementExpiry.Type;

export const CatalogueFamilyGrouping = Schema.Literals(CATALOGUE_FAMILY_GROUPINGS);
export type CatalogueFamilyGrouping = typeof CatalogueFamilyGrouping.Type;

export const CataloguePhaseLabelSource = Schema.Literals(CATALOGUE_PHASE_LABEL_SOURCES);
export type CataloguePhaseLabelSource = typeof CataloguePhaseLabelSource.Type;

export const CatalogueScopeAuthority = Schema.Literals(CATALOGUE_SCOPE_AUTHORITIES);
export type CatalogueScopeAuthority = typeof CatalogueScopeAuthority.Type;

const Label = Schema.NonEmptyString;

export const EcosystemPolicy = Schema.Struct({
  /** The ecosystem this policy governs. Equals its key in the catalog. */
  ecosystem: Ecosystem,

  // --- Identity and operator-facing naming ------------------------------------------------
  /** The authority that publishes the regulation and operates the Directory. */
  regulator: EcosystemRegulator,
  /** Full product name, used where the screen has room for it. */
  displayName: Label,
  /** The API family name, used in the ecosystem switch and other tight chrome. */
  apiFamilyName: Label,
  /** The uppercase acronym, used in dense tables and select options. */
  shortLabel: Label,

  // --- Regulated endpoints and trust ------------------------------------------------------
  // The participant-Directory HOSTS are deliberately NOT an axis: a deployment overrides them
  // through `ONBOARDING_DIRECTORY_*` to target a private mirror, so they are configuration with
  // verified defaults (`services/power/.../ssa-directory/token/hosts.ts`), not a fact about the
  // ecosystem. Stating them here as well produced two host tables that drifted.
  /** The Keycloak realm that carries this ecosystem's authorisation server. */
  authorisationServerRealm: AuthorisationServerRealm,
  /** Which bounded context's Postgres schema holds this ecosystem's participant directory. */
  directoryBoundedContext: DirectoryBoundedContext,
  /** Which organisation identifier a Directory-issued software statement carries. */
  softwareStatementOrgIdentifier: SoftwareStatementOrgIdentifier,
  /** Whether a Directory-issued software statement carries its own `exp`. */
  softwareStatementExpiry: SoftwareStatementExpiry,

  // --- Regulatory catalogue ---------------------------------------------------------------
  /** How the generated family rows are grouped into catalogue families. */
  catalogueFamilyGrouping: CatalogueFamilyGrouping,
  /** Where a catalogue family's phase label comes from. */
  cataloguePhaseLabelSource: CataloguePhaseLabelSource,
  /** Which claim decides whether a customer may run a catalogue family. */
  catalogueScopeAuthority: CatalogueScopeAuthority,
}).annotate({
  identifier: 'EcosystemPolicy',
  description: 'Every decision a regulatory ecosystem makes, stated once per ecosystem.',
});
export type EcosystemPolicy = typeof EcosystemPolicy.Type;
